// src/components/ProtectedRoute.js
//
// ProtectedRoute
// ==============
//
// PURPOSE
// -------
// Route guard for authenticated routes and optional role-based authorization.
//
// This component is intentionally a PURE ROUTE GUARD.
// It does NOT:
//
// - call backend logout
// - call handleLogout()
// - call softLogout()
// - perform refresh logic
// - repair session issues
// - try to discover or restore sessions
//
// Those responsibilities already belong to:
// - AuthContext
// - apiClient
// - backend auth/session validation
//
// CURRENT APP ARCHITECTURE
// ------------------------
// Auth flow in the application:
//
// 1) AuthContext bootstraps current auth state
// 2) ProtectedRoute waits until bootstrap finishes
// 3) After bootstrap:
//    - authenticated user may proceed
//    - unauthenticated user is redirected to login
//    - optionally, role requirement is enforced
//
// STRICT TAB-SELECTED AUTH ALIGNMENT
// ----------------------------------
// This component is fully compatible with the strict tab-selected model.
//
// That means:
// - ProtectedRoute does not care about cookies directly
// - ProtectedRoute does not care about sessionStorage directly
// - ProtectedRoute trusts the final tab-local auth decision from AuthContext
//
// Therefore:
//
// - manually opening a protected URL in a fresh tab
//   -> bootstrap ends unauthenticated for that tab
//   -> ProtectedRoute redirects to login
//
// - duplicating a tab where sessionIdentity/AuthContext stripped local authority
//   -> bootstrap ends unauthenticated for that duplicate tab
//   -> ProtectedRoute redirects to login
//
// - explicit login in THAT tab
//   -> AuthContext becomes authenticated
//   -> ProtectedRoute allows entry
//
// ROUTER NOTE
// -----------
// App uses:
//
//   <Router basename="/app">
//
// Therefore all routes here are INTERNAL router paths only,
// without "/app" prefix.
//

import React, { useContext } from "react";
import { Navigate, useLocation } from "react-router-dom";
import AuthContext from "../context/AuthContext";

const DEV = process.env.NODE_ENV !== "production";

/* -------------------------------------------------------------------------- */
/* Route constants                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Internal login route.
 *
 * With basename="/app", this maps to browser URL:
 *   /app/
 */
const LOGIN_ROUTE = "/";

/**
 * Safe fallback for authenticated-but-forbidden users.
 *
 * We do NOT send such users back to login because they are already authenticated.
 */
const FORBIDDEN_FALLBACK_ROUTE = "/dashboard";

/* -------------------------------------------------------------------------- */
/* Small helpers                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Normalize route role requirements into a lowercased array.
 *
 * Supported input:
 * - undefined / null
 * - "superuser"
 * - ["superuser", "admin"]
 *
 * Returns:
 * - null if there is no valid requirement
 * - array of normalized roles otherwise
 *
 * Why normalize?
 * - route definitions stay flexible
 * - comparisons become case-insensitive and predictable
 * - minor spelling/casing inconsistencies are less likely to break access checks
 */
function normalizeRoles(requiredRole) {
  if (!requiredRole) return null;

  const raw = Array.isArray(requiredRole) ? requiredRole : [requiredRole];

  const normalized = raw
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);

  return normalized.length > 0 ? normalized : null;
}

/**
 * Normalize user role for safe comparison.
 *
 * We keep this helper separate so role comparison logic stays easy to read.
 */
function normalizeUserRole(role) {
  return String(role || "").trim().toLowerCase();
}

/**
 * Minimal bootstrap fallback.
 *
 * Kept intentionally tiny:
 * - avoids layout complexity inside route guard
 * - easy to replace later with branded spinner/skeleton
 * - prevents route flicker while bootstrap is still unresolved
 */
function AuthBootstrapFallback() {
  return (
    <div style={{ padding: 16 }}>
      <span>Loading…</span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

const ProtectedRoute = ({ children, requiredRole }) => {
  const { authState, authChecked } = useContext(AuthContext);
  const location = useLocation();

  /* ------------------------------------------------------------------------ */
  /* 1) Wait for initial auth bootstrap                                       */
  /* ------------------------------------------------------------------------ */

  /**
   * This prevents redirect flicker during page refresh.
   *
   * Without this wait:
   * - page reload starts with unknown auth state
   * - route guard might redirect too early
   * - AuthContext could authenticate correctly a moment later
   *
   * In strict tab-selected auth this is especially important because
   * bootstrap now has a deterministic "authenticated or not for THIS TAB"
   * decision and ProtectedRoute should wait for that decision exactly once.
   *
   * Important:
   * This loading state is NOT a recovery mechanism.
   * It is only a short guard while AuthContext finishes its current-tab decision.
   */
  if (!authChecked) {
    if (DEV) {
      // eslint-disable-next-line no-console
      console.log("[ProtectedRoute] Waiting for auth bootstrap...");
    }

    return <AuthBootstrapFallback />;
  }

  /* ------------------------------------------------------------------------ */
  /* 2) Not authenticated -> redirect to login                                */
  /* ------------------------------------------------------------------------ */

  /**
   * Once bootstrap has completed, authState.isAuthenticated is meaningful.
   *
   * We preserve the originally requested route in state.from.
   * LoginPage can then redirect the user back after successful login.
   *
   * This is what enables the required flow:
   *
   *   user manually opens protected URL
   *   -> ProtectedRoute redirects to login with state.from
   *   -> user logs in explicitly in THIS tab
   *   -> LoginPage navigates back to original target
   *
   * This also cleanly supports:
   * - duplicate-tab -> login
   * - manual URL -> login
   * - fresh tab -> login
   *
   * Because ProtectedRoute does not try to be smart.
   * It simply enforces the current AuthContext result for THIS tab.
   */
  if (!authState?.isAuthenticated) {
    if (DEV) {
      // eslint-disable-next-line no-console
      console.warn("[ProtectedRoute] Not authenticated -> redirect to login.", {
        path: location.pathname,
        reason: "AUTH_REQUIRED",
      });
    }

    return (
      <Navigate
        to={LOGIN_ROUTE}
        replace
        state={{
          from: location,
          reason: "AUTH_REQUIRED",
        }}
      />
    );
  }

  /* ------------------------------------------------------------------------ */
  /* 3) Optional role-based authorization                                     */
  /* ------------------------------------------------------------------------ */

  /**
   * If no requiredRole is specified, any authenticated user may proceed.
   *
   * This keeps route definitions simple:
   * - authentication-only routes need no extra config
   * - restricted routes can still declare one role or many roles
   */
  const allowedRoles = normalizeRoles(requiredRole);

  if (allowedRoles) {
    const currentUserRole = normalizeUserRole(authState?.user?.role);
    const isAllowed =
      Boolean(currentUserRole) && allowedRoles.includes(currentUserRole);

    if (!isAllowed) {
      if (DEV) {
        // eslint-disable-next-line no-console
        console.warn("[ProtectedRoute] Forbidden route access.", {
          path: location.pathname,
          userRole: currentUserRole || "(none)",
          requiredRoles: allowedRoles,
        });
      }

      /**
       * Important distinction:
       * - user IS authenticated
       * - user is simply NOT authorized for THIS page
       *
       * Therefore we redirect to a safe internal route,
       * not back to login.
       *
       * This avoids:
       * - confusing the user into thinking they were logged out
       * - creating redirect loops between forbidden page and login
       */
      return (
        <Navigate
          to={FORBIDDEN_FALLBACK_ROUTE}
          replace
          state={{
            from: location,
            reason: "FORBIDDEN",
          }}
        />
      );
    }
  }

  /* ------------------------------------------------------------------------ */
  /* 4) Authorized -> render route content                                    */
  /* ------------------------------------------------------------------------ */

  /**
   * At this point:
   * - auth bootstrap is finished
   * - the user is authenticated in THIS tab
   * - any optional role requirement has passed
   *
   * Therefore the protected page may render normally.
   */
  return children;
};

export default ProtectedRoute;